/* LOBO CUSTOM C46: datos de factura en la página del carrito.
   Los campos llevan form="cart", así que viajan como atributos del carrito en el POST de
   "Finalizar compra". Este elemento valida, muestra los errores junto a cada campo, bloquea
   el envío si falta algo y guarda los datos en el carrito mientras se escriben. */
if (!customElements.get("lobo-invoice-fields")) {
  const RUT_FORMAT = /^(\d{7,8})-([\dK])$/;
  const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Dígito verificador por módulo 11 (serie 2 a 7 desde la derecha)
  const rutDigit = (body) => {
    let sum = 0;
    let factor = 2;
    for (let i = body.length - 1; i >= 0; i--) {
      sum += Number(body[i]) * factor;
      factor = factor === 7 ? 2 : factor + 1;
    }
    const rest = 11 - (sum % 11);
    return rest === 11 ? "0" : rest === 10 ? "K" : String(rest);
  };

  // Formato automático del RUT: solo dígitos y una K final (como máximo 8 dígitos
  // de cuerpo + dígito verificador), sin puntos ni espacios, con guion antes del último carácter.
  // "17.603.954-k" y "17603954K" quedan como "17603954-K".
  const RUT_MAX_CHARS = 9;
  const formatRut = (raw) => {
    let clean = "";
    for (const char of raw.toUpperCase()) {
      if (/\d/.test(char)) {
        clean += char;
      } else if (char === "K" && clean) {
        clean += char; // la K solo vale como dígito verificador: cierra el RUT
        break;
      }
      if (clean.length >= RUT_MAX_CHARS) break;
    }
    return clean.length > 1 ? `${clean.slice(0, -1)}-${clean.slice(-1)}` : clean;
  };
  const isRutChar = (char) => /[\dK]/i.test(char);

  const RULES = {
    rut: (value) => {
      const match = RUT_FORMAT.exec(value);
      if (!match) return "Ingresa tu RUT.";
      if (rutDigit(match[1]) !== match[2]) {
        return "Revisa tu RUT, el dígito verificador no coincide.";
      }
      return "";
    },
    giro: (value) =>
      value.length >= 3 ? "" : "Ingresa el giro de tu empresa.",
    address: (value) =>
      value.length >= 5
        ? ""
        : "Ingresa la dirección: calle, número y comuna.",
    company: () => "",
    email: (value) =>
      !value || EMAIL_FORMAT.test(value) ? "" : "Escribe un correo válido.",
  };

  class LoboInvoiceFields extends HTMLElement {
    constructor() {
      super();
      this.touched = new WeakSet();
      this.onSubmit = this.onSubmit.bind(this);
      this.save = debounce(this.save.bind(this), 600);
    }

    connectedCallback() {
      this.form = document.getElementById("cart");
      this.inputs = [...this.querySelectorAll("input[data-validate]")];

      this.inputs.forEach((input) => {
        if (input.dataset.validate === "rut") {
          input.value = formatRut(input.value);
        }
        input.addEventListener("blur", () => {
          this.normalize(input);
          this.touched.add(input);
          this.validate(input);
        });
        input.addEventListener("input", (event) => {
          if (input.dataset.validate === "rut" && !event.isComposing) {
            this.formatRutInput(input);
          }
          if (this.touched.has(input)) this.validate(input);
          this.save();
        });
        input.addEventListener("change", () => {
          this.normalize(input);
          this.save();
        });
      });

      // Captura: corre antes que cualquier otro listener del formulario
      this.form?.addEventListener("submit", this.onSubmit, true);
    }

    disconnectedCallback() {
      this.form?.removeEventListener("submit", this.onSubmit, true);
    }

    // Reformatea el RUT mientras se escribe o se pega y deja el cursor junto al mismo carácter
    formatRutInput(input) {
      const raw = input.value;
      const caret = input.selectionStart ?? raw.length;
      const formatted = formatRut(raw);
      if (formatted === raw) return;

      // cuántos dígitos o K había a la izquierda del cursor
      let before = 0;
      for (const char of raw.slice(0, caret)) if (isRutChar(char)) before++;
      before = Math.min(before, RUT_MAX_CHARS);

      input.value = formatted;
      let position = 0;
      for (let seen = 0; position < formatted.length && seen < before; position++) {
        if (isRutChar(formatted[position])) seen++;
      }
      input.setSelectionRange(position, position);
    }

    normalize(input) {
      let value = input.value.trim();
      if (input.dataset.validate === "rut") {
        value = value.replace(/\s+/g, "").toUpperCase();
      }
      if (value !== input.value) input.value = value;
    }

    validate(input) {
      const message = RULES[input.dataset.validate](input.value.trim());
      const error = this.querySelector(`#${input.getAttribute("aria-describedby")}`);
      input.classList.toggle("is-invalid", Boolean(message));
      if (message) {
        input.setAttribute("aria-invalid", "true");
      } else {
        input.removeAttribute("aria-invalid");
      }
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
      return !message;
    }

    onSubmit(event) {
      const submitter = event.submitter;
      if (submitter && submitter.name !== "checkout") return;

      let firstInvalid = null;
      this.inputs.forEach((input) => {
        this.normalize(input);
        this.touched.add(input);
        if (!this.validate(input) && !firstInvalid) firstInvalid = input;
      });

      if (firstInvalid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        firstInvalid.scrollIntoView({ block: "center", behavior: "smooth" });
        firstInvalid.focus({ preventScroll: true });
      }
    }

    // Guarda lo escrito en los atributos del carrito (no bloquea nada: eso lo hace la validación)
    save() {
      const attributes = {};
      this.inputs.forEach((input) => {
        attributes[decodeName(input.name)] = input.value.trim();
      });
      fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ attributes }),
      }).catch(() => {});
    }
  }

  // name="attributes[Dirección de facturación]" -> "Dirección de facturación"
  const decodeName = (name) => name.replace(/^attributes\[(.*)\]$/, "$1");

  customElements.define("lobo-invoice-fields", LoboInvoiceFields);
}
